import React, { useCallback, useEffect, useState } from 'react';
import {
  Star, Plus, RefreshCw, Edit2, Trash2, X, Save, Image as ImageIcon,
} from 'lucide-react';
import { adminService } from '../services/adminService';
import type { NarrativeItem, NarrativeItemType } from '../types';
import { formatDateTime } from '../utils/format';
import './AdminPage.css';

const TYPE_OPTIONS: { id: NarrativeItemType | ''; label: string }[] = [
  { id: '', label: 'All types' },
  { id: 'press', label: 'Press' },
  { id: 'award', label: 'Award' },
  { id: 'partnership', label: 'Partnership' },
  { id: 'endorsement', label: 'Endorsement' },
  { id: 'milestone', label: 'Milestone' },
];

interface FormState {
  type: NarrativeItemType;
  title: string;
  body: string;
  source: string;
  url: string;
  image_url: string;
  occurred_at: string;
  is_featured: boolean;
}

const EMPTY_FORM: FormState = {
  type: 'press',
  title: '',
  body: '',
  source: '',
  url: '',
  image_url: '',
  occurred_at: new Date().toISOString().slice(0, 10),
  is_featured: true,
};

const Press: React.FC = () => {
  const [items, setItems] = useState<NarrativeItem[]>([]);
  const [filterType, setFilterType] = useState<NarrativeItemType | ''>('');
  const [featuredOnly, setFeaturedOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [editing, setEditing] = useState<NarrativeItem | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const res = await adminService.listNarrativeItems({
        type: filterType || undefined,
        featured_only: featuredOnly || undefined,
        limit: 200,
      });
      setItems(res?.items || []);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to load');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [filterType, featuredOnly]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setEditing(null);
    setCreating(true);
    setError(null);
  };

  const openEdit = (item: NarrativeItem) => {
    setForm({
      type: item.type,
      title: item.title,
      body: item.body || '',
      source: item.source || '',
      url: item.url || '',
      image_url: item.image_url || '',
      occurred_at: item.occurred_at ? item.occurred_at.slice(0, 10) : '',
      is_featured: item.is_featured,
    });
    setEditing(item);
    setCreating(false);
    setError(null);
  };

  const closeModal = () => {
    setEditing(null);
    setCreating(false);
    setError(null);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const payload = {
        type: form.type,
        title: form.title.trim(),
        body: form.body.trim() || undefined,
        source: form.source.trim() || undefined,
        url: form.url.trim() || undefined,
        image_url: form.image_url.trim() || undefined,
        occurred_at: form.occurred_at ? new Date(form.occurred_at).toISOString() : undefined,
        is_featured: form.is_featured,
      };
      if (editing) {
        await adminService.updateNarrativeItem(editing.id, payload);
      } else {
        await adminService.createNarrativeItem(payload as any);
      }
      closeModal();
      load();
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item: NarrativeItem) => {
    if (!window.confirm(`Delete "${item.title}"? This can't be undone.`)) return;
    try {
      await adminService.deleteNarrativeItem(item.id);
      load();
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to delete');
    }
  };

  const handleToggleFeatured = async (item: NarrativeItem) => {
    try {
      await adminService.updateNarrativeItem(item.id, { is_featured: !item.is_featured });
      load();
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to update');
    }
  };

  const isModalOpen = creating || editing !== null;

  return (
    <div className="admin-page">
      <div className="page-header">
        <div>
          <h1><Star size={20} style={{ marginRight: 8 }} />Press & Accolades</h1>
          <p className="page-subtitle">
            Curate the press, partnerships and milestones that appear in the Investor / Visa Brief.
          </p>
        </div>
        <div className="header-actions">
          <select value={filterType} onChange={(e) => setFilterType(e.target.value as NarrativeItemType | '')}>
            {TYPE_OPTIONS.map((o) => (
              <option key={o.id} value={o.id}>{o.label}</option>
            ))}
          </select>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.85rem', color: '#475569' }}>
            <input type="checkbox" checked={featuredOnly} onChange={(e) => setFeaturedOnly(e.target.checked)} />
            featured only
          </label>
          <button className="btn secondary" onClick={load} disabled={refreshing}>
            <RefreshCw size={14} className={refreshing ? 'spin' : ''} /> {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
          <button className="btn" onClick={openCreate}>
            <Plus size={14} /> New item
          </button>
        </div>
      </div>

      {error && (
        <div className="panel">
          <div className="panel-body" style={{ color: '#b91c1c', fontSize: '0.85rem' }}>{error}</div>
        </div>
      )}

      <div className="panel">
        <div className="panel-body tight">
          {loading ? (
            <div className="skeleton" style={{ height: 240, margin: 16 }} />
          ) : items.length === 0 ? (
            <div className="empty">
              No narrative items yet — click "New item" to add the first press mention or milestone.
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Image</th>
                  <th>Title</th>
                  <th>Type</th>
                  <th>Source</th>
                  <th>Occurred</th>
                  <th>Featured</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id}>
                    <td>
                      {item.image_url ? (
                        <img src={item.image_url} alt="" style={{ width: 60, height: 36, objectFit: 'cover', borderRadius: 4 }} />
                      ) : (
                        <div style={{
                          width: 60, height: 36, borderRadius: 4,
                          background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8',
                        }}>
                          <ImageIcon size={16} />
                        </div>
                      )}
                    </td>
                    <td style={{ maxWidth: 320 }}>
                      <div style={{ fontWeight: 600 }}>{item.title}</div>
                      {item.body && (
                        <div style={{ color: '#64748b', fontSize: '0.8rem', marginTop: 2 }}>
                          {item.body.length > 120 ? `${item.body.slice(0, 120)}…` : item.body}
                        </div>
                      )}
                    </td>
                    <td><span className={`pill ${item.type}`}>{item.type}</span></td>
                    <td>
                      {item.source}
                      {item.url && (
                        <a href={item.url} target="_blank" rel="noreferrer" style={{ marginLeft: 8, color: '#410C6D', fontSize: 12 }}>open</a>
                      )}
                    </td>
                    <td>{item.occurred_at ? formatDateTime(item.occurred_at) : '—'}</td>
                    <td>
                      <button
                        onClick={() => handleToggleFeatured(item)}
                        title={item.is_featured ? 'Click to unfeature' : 'Click to feature'}
                        style={{
                          background: 'none', border: 'none', cursor: 'pointer',
                          color: item.is_featured ? '#f59e0b' : '#cbd5e1',
                        }}
                      >
                        <Star size={18} fill={item.is_featured ? '#f59e0b' : 'transparent'} />
                      </button>
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <button className="btn secondary" style={{ padding: '4px 8px', marginRight: 6 }} onClick={() => openEdit(item)}>
                        <Edit2 size={12} />
                      </button>
                      <button className="btn danger" style={{ padding: '4px 8px' }} onClick={() => handleDelete(item)}>
                        <Trash2 size={12} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {isModalOpen && (
        <div className="vb-modal-overlay" onClick={closeModal}>
          <div className="vb-modal" onClick={(e) => e.stopPropagation()}>
            <div className="vb-modal-header">
              <h3>{editing ? 'Edit narrative item' : 'New narrative item'}</h3>
              <button className="btn secondary" onClick={closeModal}><X size={14} /></button>
            </div>
            <div className="vb-modal-body">
              <div className="vb-form-row">
                <label>Type</label>
                <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value as NarrativeItemType })}>
                  <option value="press">Press</option>
                  <option value="award">Award</option>
                  <option value="partnership">Partnership</option>
                  <option value="endorsement">Endorsement</option>
                  <option value="milestone">Milestone</option>
                </select>
              </div>
              <div className="vb-form-row">
                <label>Title</label>
                <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
              </div>
              <div className="vb-form-row">
                <label>Body</label>
                <textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} rows={4} />
              </div>
              <div className="vb-form-row vb-form-row-2">
                <div>
                  <label>Source</label>
                  <input value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} />
                </div>
                <div>
                  <label>Occurred at</label>
                  <input type="date" value={form.occurred_at} onChange={(e) => setForm({ ...form, occurred_at: e.target.value })} />
                </div>
              </div>
              <div className="vb-form-row">
                <label>URL</label>
                <input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} />
              </div>
              <div className="vb-form-row">
                <label>Image URL</label>
                <input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} />
                {form.image_url && (
                  <img src={form.image_url} alt="" style={{ width: '100%', maxHeight: 140, objectFit: 'cover', marginTop: 6, borderRadius: 6 }} />
                )}
              </div>
              <div className="vb-form-row">
                <label style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                  <input type="checkbox" checked={form.is_featured} onChange={(e) => setForm({ ...form, is_featured: e.target.checked })} />
                  Show in Visa Brief (featured)
                </label>
              </div>
              {error && <div style={{ color: '#b91c1c', fontSize: 13 }}>{error}</div>}
            </div>
            <div className="vb-modal-footer">
              <button className="btn secondary" onClick={closeModal}>Cancel</button>
              <button className="btn" onClick={handleSave} disabled={saving || !form.title.trim()}>
                <Save size={14} /> {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .vb-modal-overlay {
          position: fixed; inset: 0;
          background: rgba(15, 23, 42, 0.55);
          display: flex; align-items: center; justify-content: center;
          z-index: 1000;
        }
        .vb-modal {
          background: white;
          border-radius: 12px;
          max-width: 560px;
          width: calc(100% - 40px);
          max-height: 90vh;
          display: flex; flex-direction: column;
          overflow: hidden;
        }
        .vb-modal-header, .vb-modal-footer {
          display: flex; justify-content: space-between; align-items: center;
          padding: 14px 18px;
          border-bottom: 1px solid #e5e7eb;
        }
        .vb-modal-footer { border-top: 1px solid #e5e7eb; border-bottom: none; }
        .vb-modal-header h3 { margin: 0; font-size: 1rem; }
        .vb-modal-body {
          padding: 16px 18px; flex: 1; overflow-y: auto;
          display: flex; flex-direction: column; gap: 12px;
        }
        .vb-form-row { display: flex; flex-direction: column; gap: 4px; }
        .vb-form-row label { font-size: 0.78rem; font-weight: 600; color: #475569; }
        .vb-form-row input,
        .vb-form-row textarea,
        .vb-form-row select {
          width: 100%;
          padding: 8px 10px;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          font-size: 0.9rem;
          font-family: inherit;
        }
        .vb-form-row-2 { flex-direction: row; gap: 12px; }
        .vb-form-row-2 > div { flex: 1; display: flex; flex-direction: column; gap: 4px; }

        :root.dark .vb-modal { background: #1e293b; color: #f1f5f9; }
        :root.dark .vb-modal-header, :root.dark .vb-modal-footer { border-color: #334155; }
        :root.dark .vb-form-row label { color: #cbd5e1; }
        :root.dark .vb-form-row input,
        :root.dark .vb-form-row textarea,
        :root.dark .vb-form-row select {
          background: #0f172a;
          border-color: #334155;
          color: #f1f5f9;
        }
      `}</style>
    </div>
  );
};

export default Press;
